import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  CopyOutlined, CheckOutlined, MenuOutlined, CloseOutlined,
  RocketOutlined, CodeOutlined, AppstoreOutlined,
  KeyOutlined, ThunderboltOutlined,
  ArrowLeftOutlined, CloudServerOutlined,
  SettingOutlined, MonitorOutlined,
  SafetyOutlined, DesktopOutlined,
  LockOutlined, DatabaseOutlined,
} from '@ant-design/icons';

/* ───────────────────────── Sidebar sections ───────────────────────── */

const SECTIONS = [
  { id: 'intro', label: 'Introducción', icon: <CloudServerOutlined /> },
  { id: 'quick-start', label: 'Inicio Rápido', icon: <RocketOutlined /> },
  { id: 'os', label: 'Sistemas Operativos', icon: <DesktopOutlined /> },
  { id: 'instances', label: 'Tipos de Instancia', icon: <AppstoreOutlined /> },
  { id: 'access', label: 'Métodos de Acceso', icon: <KeyOutlined /> },
  { id: 'terminal', label: 'Terminal Web', icon: <CodeOutlined /> },
  { id: 'credentials', label: 'Credenciales', icon: <LockOutlined /> },
  { id: 'security', label: 'Reglas de Seguridad', icon: <SafetyOutlined /> },
  { id: 'statuses', label: 'Estados del Servicio', icon: <ThunderboltOutlined /> },
  { id: 'events', label: 'Eventos', icon: <MonitorOutlined /> },
];

/* ───────────────────────── Code block ───────────────────────── */

const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="relative group rounded-lg overflow-hidden my-4">
      {language && (
        <div className="bg-gray-800 dark:bg-gray-950 px-4 py-2 text-xs text-gray-400 font-mono border-b border-gray-700">{language}</div>
      )}
      <pre className="bg-gray-900 dark:bg-gray-950 text-gray-100 px-4 py-4 text-sm font-mono overflow-x-auto leading-relaxed"><code>{code}</code></pre>
      <button onClick={handleCopy} className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md text-gray-300 hover:text-white transition-colors opacity-0 group-hover:opacity-100" title="Copiar">
        {copied ? <CheckOutlined className="text-green-400" /> : <CopyOutlined />}
      </button>
    </div>
  );
};

/* ───────────────────────── Main component ───────────────────────── */

const DocsVM: React.FC = () => {
  const [activeSection, setActiveSection] = useState('intro');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const offsets = SECTIONS.map(({ id }) => {
        const el = document.getElementById(id);
        return { id, top: el ? el.getBoundingClientRect().top : Infinity };
      });
      const current = offsets.reduce((closest, section) =>
        section.top <= 120 && section.top > closest.top ? section : closest,
        { id: offsets[0].id, top: -Infinity }
      );
      setActiveSection(current.id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'start' }); setMobileMenuOpen(false); }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-gray-900/80 backdrop-blur-md border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="flex items-center gap-2.5 hover:opacity-80 transition-opacity">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <img src="/favicon-logo.png" alt="Kubiy" className="w-5 h-5" />
              </div>
              <span className="font-semibold text-gray-900 dark:text-white">Kubiy Cloud</span>
            </Link>
            <div className="h-5 w-px bg-gray-300 dark:bg-gray-700"></div>
            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">Virtual Machines</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors">
              <ArrowLeftOutlined /> Volver al inicio
            </Link>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white">
              {mobileMenuOpen ? <CloseOutlined /> : <MenuOutlined />}
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex gap-8">
          {/* Sidebar */}
          <aside className={`${mobileMenuOpen ? 'fixed inset-0 top-16 z-20 bg-white dark:bg-gray-900 p-6' : 'hidden'} lg:block lg:w-64 lg:flex-shrink-0 lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:overflow-y-auto lg:py-8`}>
            <nav className="space-y-1">
              {SECTIONS.map((section) => (
                <button key={section.id} onClick={() => scrollTo(section.id)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left ${activeSection === section.id ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-white'}`}>
                  <span className="text-base">{section.icon}</span>
                  {section.label}
                </button>
              ))}
            </nav>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0 py-8 lg:py-12">
            <div className="max-w-3xl">

              {/* ═══════════════ INTRODUCCIÓN ═══════════════ */}
              <section id="intro" className="mb-16 scroll-mt-24">
                <div className="flex items-center gap-3 mb-2">
                  <span className="px-3 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full text-xs font-semibold">Documentación</span>
                </div>
                <h1 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">Virtual Machines</h1>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed mb-6">
                  Kubiy Virtual Machines te permite crear y gestionar máquinas virtuales en la nube de AWS.
                  Elige tu sistema operativo, tipo de instancia, almacenamiento y método de acceso.
                  Administra reglas de seguridad, descarga credenciales y conéctate vía terminal web o SSH.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <DesktopOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Multi-OS</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Amazon Linux, Ubuntu, Debian, RHEL, SUSE, Windows</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <SafetyOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Acceso Seguro</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">IP Pública con firewall o SSM sin exposición</p>
                  </div>
                  <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
                    <CodeOutlined className="text-xl text-indigo-500 mb-2" />
                    <h3 className="font-semibold text-sm mb-1">Terminal Web</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Conéctate desde el navegador sin cliente SSH</p>
                  </div>
                </div>
              </section>

              {/* ═══════════════ INICIO RÁPIDO ═══════════════ */}
              <section id="quick-start" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Inicio Rápido</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">Crea tu primera máquina virtual en 3 sencillos pasos:</p>
                <div className="space-y-6">
                  {[
                    { n: 1, title: 'Selecciona el sistema operativo', desc: 'Desde el dashboard, ve a Máquinas Virtuales → Crear. Asigna un nombre y selecciona el SO. Puedes filtrar por tipo (Linux, Windows) o distribución específica.' },
                    { n: 2, title: 'Configura la instancia', desc: 'Elige el tipo de instancia (CPU, RAM), configura almacenamiento (mín. 60 GB), activa backups y monitoreo, y selecciona el método de acceso: IP Pública o SSM.' },
                    { n: 3, title: 'Revisa y crea', desc: 'Confirma la configuración. Se genera una orden de pago. Después del pago, Kubiy provisiona automáticamente la red, instancia, reglas de seguridad y credenciales en 3-10 minutos.' },
                  ].map((step) => (
                    <div key={step.n} className="flex gap-4">
                      <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-full flex items-center justify-center text-sm font-bold">{step.n}</div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* ═══════════════ SISTEMAS OPERATIVOS ═══════════════ */}
              <section id="os" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Sistemas Operativos Soportados</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Distribuciones Linux</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Sistema Operativo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Usuario SSH</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Amazon Linux 2', 'ec2-user', 'Distribución optimizada para AWS, ligera y con soporte LTS'],
                        ['Ubuntu 22.04 LTS', 'ubuntu', 'Distribución popular con amplia comunidad y repositorios'],
                        ['Ubuntu 20.04 LTS', 'ubuntu', 'Versión LTS anterior, estable con soporte extendido'],
                        ['Debian', 'admin', 'Distribución estable y minimalista'],
                        ['RHEL', 'ec2-user', 'Distribución empresarial con soporte comercial'],
                        ['SUSE Linux', 'ec2-user', 'Distribución empresarial con enfoque en estabilidad'],
                      ].map(([os, user, desc]) => (
                        <tr key={os}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{os}</td>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{user}</code></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Windows</h3>
                <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Windows Server</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Servidor Windows con interfaz gráfica. Acceso mediante RDP (Remote Desktop) con credenciales descargadas desde el dashboard.
                    La terminal web no está disponible para Windows.
                  </p>
                </div>
              </section>

              {/* ═══════════════ TIPOS DE INSTANCIA ═══════════════ */}
              <section id="instances" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Tipos de Instancia</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  El tipo de instancia define la cantidad de CPU, memoria RAM y rendimiento de red de tu VM.
                </p>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">vCPU</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">RAM</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Caso de uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['t3.micro', '2', '1 GB', 'Desarrollo, pruebas, apps ligeras'],
                        ['t3.small', '2', '2 GB', 'Apps web pequeñas, microservicios'],
                        ['t3.medium', '2', '4 GB', 'Apps medianas, DBs pequeñas'],
                        ['t3.large', '2', '8 GB', 'Servidores de aplicaciones'],
                        ['t3.xlarge', '4', '16 GB', 'DBs medianas, uso intensivo de memoria'],
                        ['t3.2xlarge', '8', '32 GB', 'Cargas pesadas, DBs grandes'],
                      ].map(([type, cpu, ram, use]) => (
                        <tr key={type}>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{type}</code></td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">{cpu}</td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">{ram}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{use}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Nota:</strong> Las instancias t3 son de tipo burstable, ideales para cargas de trabajo variables.
                    Los tipos disponibles pueden variar según la región.
                  </p>
                </div>
              </section>

              {/* ═══════════════ MÉTODOS DE ACCESO ═══════════════ */}
              <section id="access" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Métodos de Acceso</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  El método de acceso se selecciona al crear la VM y determina cómo te conectas y qué nivel de exposición tiene la instancia.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-8">
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">IP Pública (Dinámica)</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      La instancia recibe IP pública, DNS público y DNS de Kubiy. Permite SSH/RDP directo desde internet.
                    </p>
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> IP pública y DNS automáticos</li>
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Reglas de seguridad configurables</li>
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> SSH directo con clave .pem</li>
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Terminal web disponible</li>
                    </ul>
                  </div>
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">SSM (Sin IP Pública)</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Sin IP pública. Acceso exclusivo vía AWS Systems Manager. Máxima seguridad sin puertos abiertos.
                    </p>
                    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Sin exposición a internet</li>
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> No requiere reglas de seguridad</li>
                      <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Terminal web disponible</li>
                      <li className="flex items-center gap-2"><CloseOutlined className="text-red-400 text-xs" /> Sin SSH/RDP directo</li>
                    </ul>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Comparación</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Característica</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">IP Pública</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">SSM</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['IP Pública', 'Sí (dinámica)', 'No'],
                        ['Terminal Web', 'Sí', 'Sí'],
                        ['SSH directo', 'Sí (con .pem)', 'No'],
                        ['RDP (Windows)', 'Sí', 'No'],
                        ['Reglas de seguridad', 'Configurables', 'No aplica'],
                        ['Nivel de seguridad', 'Medio', 'Alto'],
                      ].map(([feat, pub, ssm]) => (
                        <tr key={feat}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{feat}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{pub}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{ssm}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              {/* ═══════════════ TERMINAL WEB ═══════════════ */}
              <section id="terminal" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Terminal Web</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Kubiy proporciona una terminal web para conectarte a tu VM directamente desde el navegador
                  usando AWS Systems Manager (SSM). Disponible para VMs Linux en estado <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">running</code>.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Cómo Acceder</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Desde el detalle de tu VM, haz clic en <strong>Conectar</strong> en la barra de acciones.
                  Se abrirá la terminal web con información del servicio y red en la cabecera.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Características</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Conexión segura via WebSocket + SSM</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Modo pantalla completa</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Info del servicio y red visible en cabecera</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> Mensaje de bienvenida con datos de la instancia</li>
                </ul>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Conexión SSH Directa (IP Pública + Linux)</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  Si tu VM tiene IP pública, también puedes conectarte por SSH usando la clave <code className="bg-gray-100 dark:bg-gray-800 px-1 py-0.5 rounded text-xs font-mono">.pem</code> descargada:
                </p>
                <CodeBlock
                  language="bash"
                  code={`ssh -i C:\\Users\\TU_USUARIO\\Downloads\\nombre-vm.pem ec2-user@tu-dns-kubiy.apps1.kubiy.site`}
                />
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-300">
                    <strong>Nota:</strong> Para Windows, la terminal web no está disponible. Usa RDP con las credenciales descargadas.
                  </p>
                </div>
              </section>

              {/* ═══════════════ CREDENCIALES ═══════════════ */}
              <section id="credentials" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Credenciales de Acceso</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Kubiy genera credenciales automáticamente al crear la VM. El tipo depende del sistema operativo.
                </p>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Linux: Clave SSH (.pem)</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Se descarga como archivo .pem desde la tarjeta de conexión del panel lateral.
                    </p>
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-xs text-red-700 dark:text-red-400">
                        <strong>Solo se puede descargar una vez.</strong> Si la pierdes, no podrás recuperarla.
                      </p>
                    </div>
                  </div>
                  <div className="p-5 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Windows: Credenciales RDP</h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      Archivo de texto con usuario, contraseña e IP. Se descarga desde la tarjeta de conexión.
                    </p>
                    <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-xs text-red-700 dark:text-red-400">
                        <strong>Solo se pueden descargar una vez.</strong> Si las pierdes, no podrás recuperarlas.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* ═══════════════ REGLAS DE SEGURIDAD ═══════════════ */}
              <section id="security" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Reglas de Seguridad</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Controlan el tráfico de red hacia y desde tu VM. Solo disponibles para VMs con <strong>IP Pública</strong>.
                  Las VMs con SSM no requieren reglas ya que no exponen puertos.
                </p>

                <div className="grid sm:grid-cols-2 gap-4 mb-6">
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Reglas de Entrada (Ingress)</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Controlan qué tráfico entrante se permite. Ej: SSH, HTTP, acceso a DBs.</p>
                  </div>
                  <div className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg">
                    <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">Reglas de Salida (Egress)</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Controlan qué tráfico saliente se permite. Por defecto se permite todo.</p>
                  </div>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tipos Predefinidos</h3>
                <div className="overflow-x-auto mb-6">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Tipo</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Puerto</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Uso</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['SSH', '22', 'Conexión remota a Linux'],
                        ['RDP', '3389', 'Escritorio remoto Windows'],
                        ['HTTP', '80', 'Tráfico web'],
                        ['HTTPS', '443', 'Tráfico web cifrado'],
                        ['MySQL', '3306', 'Base de datos MySQL'],
                        ['PostgreSQL', '5432', 'Base de datos PostgreSQL'],
                        ['MSSQL', '1433', 'SQL Server'],
                        ['Custom', 'Personalizado', 'Puerto y protocolo personalizado'],
                      ].map(([type, port, use]) => (
                        <tr key={type}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{type}</td>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{port}</code></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{use}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Gestión desde el Dashboard</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-4">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Agregar:</strong> Selecciona tipo, puerto, protocolo, CIDR y descripción</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Mi IP:</strong> Botón para autocompletar tu IP pública actual</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Editar:</strong> Solo se puede modificar la descripción</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Guardar:</strong> Aplica cambios directamente al Security Group de AWS</li>
                </ul>
              </section>

              {/* ═══════════════ ESTADOS DEL SERVICIO ═══════════════ */}
              <section id="statuses" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Estados del Servicio</h2>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Estados de la VM</h3>
                <div className="overflow-x-auto mb-8">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Estado</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Indicador</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['initializing', 'bg-yellow-400 animate-pulse', 'Se provisiona red, instancia EC2, reglas y credenciales'],
                        ['deploying', 'bg-blue-400 animate-pulse', 'La infraestructura está siendo configurada'],
                        ['running', 'bg-green-500', 'VM activa. Se puede conectar, detener o reiniciar'],
                        ['stopped', 'bg-gray-400', 'Detenida manualmente. IP dinámica puede cambiar al reiniciar'],
                        ['starting', 'bg-green-400 animate-pulse', 'Arrancando después de estar detenida'],
                        ['stopping', 'bg-gray-400 animate-pulse', 'En proceso de detención'],
                        ['restarting', 'bg-blue-400 animate-pulse', 'En proceso de reinicio'],
                        ['error', 'bg-red-500', 'Error durante creación o ejecución'],
                        ['deleting', 'bg-red-400 animate-pulse', 'Eliminando todos los recursos asociados'],
                      ].map(([status, color, desc]) => (
                        <tr key={status}>
                          <td className="py-3 px-4"><code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{status}</code></td>
                          <td className="py-3 px-4"><span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`}></span></td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Acciones por Estado</h3>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-gray-200 dark:border-gray-700">
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Acción</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Disponible en</th>
                      <th className="text-left py-3 px-4 font-semibold text-gray-900 dark:text-white">Descripción</th>
                    </tr></thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {[
                        ['Conectar', 'running (Linux)', 'Abre terminal web'],
                        ['Iniciar', 'stopped', 'Arranca la VM detenida'],
                        ['Detener', 'running', 'Detiene sin eliminar infra'],
                        ['Reiniciar', 'running', 'Reinicia la instancia'],
                        ['Eliminar', 'Todos (excepto deleting)', 'Elimina permanentemente'],
                        ['Descargar clave SSH', 'running (Linux)', 'Descarga .pem'],
                        ['Descargar credenciales', 'running (Windows)', 'Descarga usuario/pass RDP'],
                      ].map(([action, avail, desc]) => (
                        <tr key={action}>
                          <td className="py-3 px-4 font-medium text-gray-900 dark:text-white">{action}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{avail}</td>
                          <td className="py-3 px-4 text-gray-600 dark:text-gray-400">{desc}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-300">
                    <strong>Tip:</strong> Durante <code className="font-mono">initializing</code> y <code className="font-mono">deploying</code>,
                    el dashboard hace polling automático. La creación toma entre 3 y 10 minutos.
                  </p>
                </div>
              </section>

              {/* ═══════════════ EVENTOS ═══════════════ */}
              <section id="events" className="mb-16 scroll-mt-24">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">Eventos del Servicio</h2>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Los eventos registran todas las acciones y cambios de estado durante el ciclo de vida de la VM.
                  Se consultan desde la pestaña <strong>Eventos</strong> en el detalle del servicio.
                </p>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Tipos de Evento</h3>
                <div className="grid sm:grid-cols-2 gap-3 mb-6">
                  {[
                    { type: 'info', color: 'bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300', desc: 'Progreso de operaciones' },
                    { type: 'success', color: 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300', desc: 'Operación completada' },
                    { type: 'warning', color: 'bg-amber-100 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', desc: 'Requiere atención' },
                    { type: 'error', color: 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300', desc: 'Error en operación' },
                  ].map((evt) => (
                    <div key={evt.type} className="flex items-center gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${evt.color}`}>{evt.type}</span>
                      <p className="text-xs text-gray-600 dark:text-gray-400">{evt.desc}</p>
                    </div>
                  ))}
                </div>

                <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Fuentes de Eventos</h3>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>Kubiy:</strong> Eventos de la plataforma (creación, eliminación, acciones del usuario)</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>CloudFormation:</strong> Eventos del stack de AWS que provisiona la infraestructura</li>
                  <li className="flex items-center gap-2"><CheckOutlined className="text-green-500 text-xs" /> <strong>System:</strong> Eventos del sistema interno</li>
                </ul>
              </section>

            </div>
          </main>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 dark:border-gray-800 mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm text-gray-500 dark:text-gray-400">&copy; {new Date().getFullYear()} Kubiy Cloud. Todos los derechos reservados.</span>
          <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400">
            <Link to="/" className="hover:text-gray-900 dark:hover:text-white transition-colors">kubiy.cloud</Link>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Términos</a>
            <span>&middot;</span>
            <a href="#" className="hover:text-gray-900 dark:hover:text-white transition-colors">Privacidad</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DocsVM;